import { get, isEmpty, isNil, sortBy, uniqBy } from "lodash";
import format from "string-template";
import {
  ACORIS_SOCIAL_REGIMES,
  COLLECTIVE_OFFER_RANK_ORDER,
  COLLECTIVE_OFFER_RANKS,
  CONTRIBUTION_STRUCTURE_MEMBERS,
  DOCUMENT_STEPS,
  PRIMARY_MEMBER_TYPES,
} from "utils/constants";
import { getDepartmentFromZipCode } from "utils/helpers/address";
import {
  filterAttachmentsByStep,
  keepLatestAttachmentsByNameAndType,
} from "utils/helpers/documentStepsConfig";
import { costToFrench } from "utils/helpers/values";
import { DEMOGRAPHIC_FIELDS, NIL_PLACEHOLDER } from "./constants";

const CORSICA_DEPARTMENT = "20";
const CORSICA_SOUTH_UPPER_BOUND = 20200;
const ZIP_CODE_LENGTH = 5;
const RATE_TO_PERCENT = 100;
const NO_FACTOR = 1;
const PRICING_REQUIRED_FIELDS = [
  "contributionStructure",
  "employeeCategory",
  "averageAge",
];

export const departmentFromZipCode = (zipCode) => {
  const department = getDepartmentFromZipCode(zipCode);
  if (department !== CORSICA_DEPARTMENT) return department;
  const digits = `${zipCode}`.replace(/\D/g, "").slice(0, ZIP_CODE_LENGTH);
  return Number(digits) < CORSICA_SOUTH_UPPER_BOUND ? "2A" : "2B";
};

export const isLocalRegime = (regime) =>
  regime === ACORIS_SOCIAL_REGIMES.ALSACE_MOSELLE;

export const withoutNil = (values) =>
  (values || []).filter((value) => !isNil(value));

export const mergeSafeList = (values, stored) => {
  const next = withoutNil(values);
  const padding = (stored || []).length - next.length;
  if (padding <= 0) return next;
  return [...next, ...Array(padding).fill(null)];
};

export const membersOfStructure = (structure) =>
  get(CONTRIBUTION_STRUCTURE_MEMBERS, structure, []);

export const demographicsFromFields = (fields) =>
  Object.fromEntries(
    Object.entries(DEMOGRAPHIC_FIELDS)
      .filter(([, field]) => !isNil(get(fields, field)))
      .map(([key, field]) => [key, get(fields, field)]),
  );

export const fieldsFromDemographics = (demographics) =>
  Object.fromEntries(
    Object.entries(DEMOGRAPHIC_FIELDS).map(([key, field]) => [
      field,
      get(demographics, key, null),
    ]),
  );

export const collectiveCatalog = (plans, offers) => {
  const levelPlans = sortBy(
    (plans || []).filter(
      ({ isStandalone, level }) => isStandalone !== false && !isNil(level),
    ),
    "level",
  );
  const offeredUpsellIds = {};
  const upsellEntries = [];
  for (const offer of offers || []) {
    const entries = get(offer, "offerInsurancePlans") || [];
    const main = entries.find((entry) => entry.main);
    if (isNil(get(main, "insurancePlan.id"))) continue;
    const upsells = sortBy(
      entries.filter((entry) => !entry.main && entry.insurancePlan),
      "weight",
    );
    offeredUpsellIds[main.insurancePlan.id] = upsells.map(
      ({ insurancePlan }) => insurancePlan.id,
    );
    upsellEntries.push(...upsells);
  }
  const upsellPlans = uniqBy(
    sortBy(upsellEntries, "weight"),
    "insurancePlan.id",
  ).map(({ insurancePlan }) => insurancePlan);
  return { levelPlans, upsellPlans, offeredUpsellIds };
};

export const levelsByPlanId = (plans) =>
  Object.fromEntries((plans || []).map(({ id, level }) => [id, level]));

export const emptyCombination = (id) => ({
  id,
  baseInsurancePlanId: null,
  upsellInsurancePlanIds: [],
});

export const combinationsWithBase = (
  combinations,
  id,
  baseInsurancePlanId,
  offeredUpsellIds,
) => {
  const index = combinations.findIndex((combination) => combination.id === id);
  return combinations.map((combination, position) => {
    if (position === index)
      return {
        ...combination,
        baseInsurancePlanId,
        upsellInsurancePlanIds: withoutNil(
          combination.upsellInsurancePlanIds,
        ).filter((upsellId) =>
          get(offeredUpsellIds, baseInsurancePlanId, []).includes(upsellId),
        ),
      };
    if (position > index && isNil(baseInsurancePlanId))
      return emptyCombination(combination.id);
    return combination;
  });
};

export const combinationsWithUpsell = (combinations, id, upsellId, checked) =>
  combinations.map((combination) => {
    if (combination.id !== id) return combination;
    const current = withoutNil(combination.upsellInsurancePlanIds);
    return {
      ...combination,
      upsellInsurancePlanIds: checked
        ? [...current, upsellId]
        : current.filter((value) => value !== upsellId),
    };
  });

export const configuredCombinations = (combinations) => {
  const configured = [];
  for (const combination of combinations || []) {
    if (isNil(get(combination, "baseInsurancePlanId"))) return configured;
    configured.push({
      id: combination.id,
      baseInsurancePlanId: combination.baseInsurancePlanId,
      upsellInsurancePlanIds: withoutNil(combination.upsellInsurancePlanIds),
    });
  }
  return configured;
};

export const persistedCombinations = (combinations, stored) =>
  mergeSafeList(
    combinations.map((combination, index) => ({
      ...combination,
      upsellInsurancePlanIds: mergeSafeList(
        combination.upsellInsurancePlanIds,
        get(stored, [index, "upsellInsurancePlanIds"]),
      ),
    })),
    stored,
  );

export const headcountsWithoutFollowing = (headcounts, rank) => {
  const kept = COLLECTIVE_OFFER_RANK_ORDER.slice(
    0,
    COLLECTIVE_OFFER_RANK_ORDER.indexOf(rank),
  );
  return Object.fromEntries(
    Object.entries(headcounts || {}).filter(([key]) => kept.includes(key)),
  );
};

export const isOptionAddDisabled = ({
  disabled,
  addableRank,
  combinations,
}) => {
  if (disabled || isNil(addableRank)) return true;
  const ids = (combinations || []).map(({ id }) => id);
  if (addableRank === COLLECTIVE_OFFER_RANKS.OPTION_1)
    return isNil(get(combinations, [0, "baseInsurancePlanId"]));
  if (addableRank === COLLECTIVE_OFFER_RANKS.OPTION_2)
    return !ids.includes(COLLECTIVE_OFFER_RANKS.OPTION_1);
  return false;
};

const includesAll = (candidate, reference) =>
  reference.every((value) => candidate.includes(value));

const addsUpsell = (candidate, reference) =>
  candidate.some((value) => !reference.includes(value));

export const combinationCompositionErrors = (combinations, levels) => {
  const errors = [];
  const list = combinations || [];
  if (isNil(get(list, [0, "baseInsurancePlanId"])))
    errors.push({ key: "COLLECTIVE_BASE_OFFER_REQUIRED", params: {} });

  const orphanIndex = list.findIndex(
    (combination, index) =>
      index > 0 &&
      !isNil(combination.baseInsurancePlanId) &&
      isNil(get(list, [index - 1, "baseInsurancePlanId"])),
  );
  if (orphanIndex > 0)
    errors.push({
      key: "COLLECTIVE_OPTION_ORDER_REQUIRED",
      params: {
        rank: list[orphanIndex].id,
        previousRank: list[orphanIndex - 1].id,
      },
    });

  const configured = configuredCombinations(list);
  for (const [index, combination] of configured.entries()) {
    if (index === 0) continue;
    const previous = configured[index - 1];
    const level = get(levels, combination.baseInsurancePlanId);
    const previousLevel = get(levels, previous.baseInsurancePlanId);
    const params = { rank: combination.id, previousRank: previous.id, level };

    if (level < previousLevel) {
      errors.push({
        key: "COLLECTIVE_OFFER_LEVEL_NOT_MONOTONIC",
        params: { ...params, previousLevel },
      });
      continue;
    }
    if (level !== previousLevel) continue;

    const upsells = combination.upsellInsurancePlanIds;
    const previousUpsells = previous.upsellInsurancePlanIds;
    if (!includesAll(upsells, previousUpsells))
      errors.push({ key: "COLLECTIVE_REINFORCEMENTS_NOT_MONOTONIC", params });
    else if (!addsUpsell(upsells, previousUpsells))
      errors.push({ key: "COLLECTIVE_OFFER_NOT_STRICTLY_RICHER", params });
  }

  return errors;
};

const allocatedPrimaryInsured = (headcounts, structure) => {
  const primaryMembers = get(PRIMARY_MEMBER_TYPES, structure, []);
  let allocated = 0;
  for (const members of Object.values(headcounts || {}))
    for (const member of primaryMembers)
      allocated += Number(get(members, member, 0)) || 0;
  return allocated;
};

export const headcountAllocationErrors = ({
  headcounts,
  structure,
  headcount,
}) => {
  if (isNil(headcount)) return [];

  const expected = Number(headcount);
  if (!Number.isFinite(expected)) return [];

  const allocated = allocatedPrimaryInsured(headcounts, structure);
  if (allocated === expected) return [];

  return [
    {
      key: "COLLECTIVE_HEADCOUNT_ALLOCATION_MISMATCH",
      params: { allocated, headcount: expected },
    },
  ];
};

export const isPricingReady = (fields) =>
  !isEmpty(configuredCombinations(get(fields, "combinations"))) &&
  PRICING_REQUIRED_FIELDS.every((field) => !isNil(get(fields, field))) &&
  (isLocalRegime(get(fields, "socialRegime")) ||
    !isNil(get(fields, "department")));

export const summaryByCombination = (costDetails) =>
  Object.fromEntries(
    get(costDetails, "summary.offers", []).map((offer) => [
      offer.combinationId || offer.rank,
      offer,
    ]),
  );

export const compositionErrorMessage = (translation, { key, params }) =>
  format(get(translation, ["COMPOSITION_ERRORS", key], key), {
    ...params,
    rank: get(translation, ["RANKS", params.rank], params.rank),
    previousRank: get(
      translation,
      ["RANKS", params.previousRank],
      params.previousRank,
    ),
  });

const frenchAmount = (value, factor) => {
  if (isNil(value)) return NIL_PLACEHOLDER;
  const amount = Number(value) * factor;
  if (!Number.isFinite(amount)) return NIL_PLACEHOLDER;
  return costToFrench(amount);
};

export const formatRate = (rate) => frenchAmount(rate, RATE_TO_PERCENT);

export const formatCurrency = (amount) => frenchAmount(amount, NO_FACTOR);

export const rangeDocumentsFromPlan = (plan, translation) => {
  const attachments = keepLatestAttachmentsByNameAndType(
    filterAttachmentsByStep({
      attachments: get(plan, "attachments") || [],
      params: get(plan, "params"),
      step: DOCUMENT_STEPS.DEVIS,
    }),
  );
  const types = get(
    translation,
    ["OFFER", "PRODUCT", "DOCUMENTS", "TYPES"],
    {},
  );

  return attachments.map((attachment) => ({
    id: attachment.id,
    name: get(types, attachment.type) || attachment.name,
    description: attachment.name || attachment.filename || "",
    url: attachment.fileUrl || null,
    type: attachment.type,
  }));
};
